import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Plus, Shield, Eye, Edit3, Loader2, X, Check, Ban,
  RotateCcw, ChevronDown,
} from "lucide-react";

const ROLE_CONFIG = {
  admin: { label: "Admin", color: "bg-purple-500/10 text-purple-400", icon: Shield, desc: "Full access" },
  sales: { label: "Sales", color: "bg-green-500/10 text-green-400", icon: Edit3, desc: "Create & edit deals" },
  editor: { label: "Editor", color: "bg-blue-500/10 text-blue-400", icon: Edit3, desc: "Edit content" },
  viewer: { label: "Viewer", color: "bg-white/5 text-white/40", icon: Eye, desc: "Read only" },
};

export default function UsersPage() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const h = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users`, { headers: h });
      setUsers(r.data);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (currentUser?.role !== "admin") {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 text-white/10 mx-auto mb-3" />
        <p className="text-white/30 text-sm">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl" data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-white" data-testid="users-title">Users</h1>
          <p className="text-white/35 text-sm mt-0.5">{users.length} users</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#C9A96E] text-white text-sm rounded-xl hover:bg-[#C9A96E]/90 transition-colors font-medium"
          data-testid="create-user-btn"
        >
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C9A96E]" /></div>
      ) : (
        <div className="space-y-2" data-testid="users-list">
          {users.map(u => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={u.id === currentUser?.id}
              isEditing={editingId === u.id}
              onEdit={() => setEditingId(editingId === u.id ? null : u.id)}
              onSave={async (data) => {
                try {
                  await axios.put(`${API}/users/${u.id}`, data, { headers: { ...h, "Content-Type": "application/json" } });
                  toast.success("User updated");
                  setEditingId(null);
                  fetchUsers();
                } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
              }}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          token={token}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchUsers(); }}
        />
      )}
    </div>
  );
}

function UserRow({ user, isSelf, isEditing, onEdit, onSave }) {
  const rc = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.is_active);
  const [showReset, setShowReset] = useState(false);
  const [newPw, setNewPw] = useState("");

  return (
    <div className={`p-3 rounded-xl border transition-colors ${isEditing ? "bg-white/[0.03] border-white/[0.08]" : "bg-white/[0.015] border-white/[0.04]"}`} data-testid={`user-${user.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${user.is_active ? "bg-gradient-to-br from-[#C9A96E] to-[#C9A96E] text-white" : "bg-white/5 text-white/20"}`}>
          {user.name?.charAt(0)?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${user.is_active ? "text-white" : "text-white/30 line-through"}`}>{user.name}</span>
            {isSelf && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#C9A96E]/10 text-[#C9A96E] font-semibold uppercase">You</span>}
            {!user.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-red-500/10 text-red-400 font-semibold uppercase">Deactivated</span>}
          </div>
          <p className="text-white/25 text-xs truncate">{user.email}</p>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg uppercase tracking-wider ${rc.color}`}>{rc.label}</span>
        {!isSelf && (
          <button onClick={onEdit} className="p-1.5 text-white/15 hover:text-white/50 rounded-lg hover:bg-white/[0.04] transition-colors" data-testid={`edit-user-${user.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isEditing && !isSelf && (
        <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold w-12">Role</label>
            <div className="flex gap-1">
              {Object.entries(ROLE_CONFIG).map(([k, v]) => (
                <button key={k} onClick={() => setRole(k)} className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold uppercase transition-colors ${role === k ? v.color : "bg-white/[0.03] text-white/20 hover:text-white/40"}`} data-testid={`role-${k}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold w-12">Status</label>
            <button onClick={() => setIsActive(!isActive)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold ${isActive ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
              {isActive ? <Check className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
              {isActive ? "Active" : "Deactivated"}
            </button>
          </div>
          {showReset ? (
            <div className="flex items-center gap-2">
              <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold w-12">PW</label>
              <input value={newPw} onChange={e => setNewPw(e.target.value)} type="password" placeholder="New password" className="bg-white/[0.04] border border-white/[0.08] text-white text-xs rounded-lg px-2.5 py-1.5 outline-none w-40" data-testid="reset-pw-input" />
            </div>
          ) : (
            <button onClick={() => setShowReset(true)} className="flex items-center gap-1.5 text-white/20 text-[10px] hover:text-white/40 transition-colors ml-14">
              <RotateCcw className="w-3 h-3" /> Reset password
            </button>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onEdit} className="px-3 py-1.5 text-white/25 text-xs rounded-lg hover:bg-white/[0.04]">Cancel</button>
            <button
              onClick={() => {
                const data = { role, is_active: isActive };
                if (newPw.trim()) data.password = newPw.trim();
                onSave(data);
              }}
              className="px-4 py-1.5 bg-[#C9A96E] text-white text-xs rounded-lg font-medium hover:bg-[#C9A96E]/90"
              data-testid="save-user-btn"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateUserModal({ token, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("sales");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) { toast.error("All fields required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/users`, { name: name.trim(), email: email.trim(), password: password.trim(), role }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      toast.success("User created!");
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || "Creation failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-user-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={handleSubmit} className="relative w-full max-w-sm bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-sm">Create User</h2>
          <button type="button" onClick={onClose} className="p-1 text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30" data-testid="new-user-name" />
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30" data-testid="new-user-email" />
          <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-xl px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30" data-testid="new-user-password" />
          <div>
            <label className="text-white/30 text-[10px] uppercase tracking-wider font-bold mb-1.5 block">Role</label>
            <div className="flex gap-1">
              {Object.entries(ROLE_CONFIG).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setRole(k)} className={`flex-1 px-2 py-2 rounded-lg text-[10px] font-semibold uppercase transition-colors ${role === k ? v.color : "bg-white/[0.03] text-white/20"}`} data-testid={`new-role-${k}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button type="submit" disabled={submitting} className="w-full mt-4 py-2.5 bg-[#C9A96E] text-white text-sm rounded-xl font-medium hover:bg-[#C9A96E]/90 disabled:opacity-30 flex items-center justify-center gap-2" data-testid="submit-create-user">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {submitting ? "Creating..." : "Create User"}
        </button>
      </form>
    </div>
  );
}
